import type { IXeroPushResult, IXeroPushRow, XeroPushKind } from "./types";

/**
 * The push routes answer on two contracts, and axiosXero can point at either
 * (api.ts getXeroApiUrl).
 *
 * The Xero service upstream (REACT_APP_XERO_API_URL) takes `{ ids }` for every
 * kind and answers one row per requested id in `results`.
 *
 * FastAPI's /v6/xero (aeronet-fast-api app/xero/api.py), the default, does that
 * for companies only. Invoices and quotes take `invoice_ids` / `quote_ids` and
 * answer with the Xero documents it created in `invoices` / `quotes`: no row
 * per id, and no reason for an id it did not create.
 */

/** Both key spellings go up; each service ignores the one it does not read. */
export const xeroPushBody = (kind: XeroPushKind, ids: number[]) => {
  switch (kind) {
    case "invoices":
      return { ids, invoice_ids: ids };
    case "quotes":
      return { ids, quote_ids: ids };
    default:
      return { ids };
  }
};

/**
 * FastAPI numbers each Xero document off the Aeronet id, and decides which
 * records it marks exported by matching on that number (client.py
 * push_invoices_to_xero `INV-{id:04d}`, push_quotes_to_xero `QU-{id:04d}`),
 * so the same number is what ties a created document back to its row here.
 */
const DOCUMENTS = {
  invoices: {
    list: "invoices",
    field: "invoice_number",
    number: (id: number) => `INV-${String(id).padStart(4, "0")}`,
  },
  quotes: {
    list: "quotes",
    field: "quote_number",
    number: (id: number) => `QU-${String(id).padStart(4, "0")}`,
  },
} as const;

export const NOT_CREATED_IN_XERO =
  "Not created in Xero. The service did not say why.";

interface IXeroPushedDocument {
  xero_id?: string | null;
  invoice_number?: string | null;
  quote_number?: string | null;
}

/** One row per requested id, in request order, whichever service answered. */
export const toXeroPushResult = (
  kind: XeroPushKind,
  ids: number[],
  data: unknown
): IXeroPushResult => {
  const body = (data ?? {}) as Record<string, unknown>;
  const batch_id = (body.batch_id as string | null | undefined) ?? null;
  if (Array.isArray(body.results)) {
    return { batch_id, results: body.results as IXeroPushRow[] };
  }
  const doc = kind === "companies" ? undefined : DOCUMENTS[kind];
  const created = doc ? body[doc.list] : undefined;
  if (!doc || !Array.isArray(created)) {
    // an answer this cannot read is a failed chunk, not "nothing was pushed"
    throw new Error(`Unrecognised answer from the Xero ${kind} push`);
  }
  const byNumber = new Map(
    (created as IXeroPushedDocument[]).map((item) => [item[doc.field], item])
  );
  return {
    batch_id,
    results: ids.map((id) => {
      const item = byNumber.get(doc.number(id));
      return item
        ? { id, ok: true, xero_id: item.xero_id ?? null, error: null }
        : { id, ok: false, xero_id: null, error: NOT_CREATED_IN_XERO };
    }),
  };
};
